import React from 'react';

/**
 * Compte rendu d'un flux CessionPME — partagé par le dépôt réel (page Diffusion) et par la
 * génération sans dépôt (Super Admin → Import/Export). Une seule mise en forme pour les deux.
 *
 * Un rapport muet ne se distingue pas d'un rapport en panne : chaque rubrique est affichée,
 * même vide, avec « rien à signaler ».
 */
export const CessionPmeRapport = ({ resultat, onRetelecharger }) => {
  if (!resultat) return null;

  if (!resultat.ok) {
    return (
      <div data-testid="cessionpme-flux-resultat"
           className="mt-3 text-xs rounded-lg border p-3 bg-red-50 border-red-200 text-red-800">
        <p className="font-semibold">
          <i className="bi bi-exclamation-triangle mr-1" />
          {resultat.message}
        </p>
      </div>
    );
  }

  const rubriques = [
    ['cessionpme-adresses-expurgees', 'Adresses expurgées du texte', resultat.adresses_expurgees],
    ['cessionpme-sans-photo', 'Biens sans aucune photo', resultat.biens_sans_photo],
    ['cessionpme-locations-loyer', 'Loyer envoyé dans « loyer »', resultat.locations_loyer],
    ['cessionpme-sans-honoraires', 'Biens sans honoraires (mention légale manquante)', resultat.biens_sans_honoraires],
  ];

  return (
    <div data-testid="cessionpme-flux-resultat"
         className="mt-3 text-xs rounded-lg border p-3 bg-white border-emerald-200 text-slate-700">
      <p className="font-semibold text-emerald-700">
        <i className="bi bi-check-circle mr-1" />
        {resultat.biens_inclus} bien(s) dans {resultat.fichier} ({resultat.octets} octets)
        {resultat.simulation && ' — aucun dépôt, fichier téléchargé'}
      </p>
      {resultat.simulation && (
        <p className="mt-1 text-slate-500">
          {resultat.agence_nom && <>Agence <strong>{resultat.agence_nom}</strong>. </>}
          Archive <strong>{resultat.fichier}</strong> contenant <strong>{resultat.fichier_xml}</strong>.
          {!resultat.configure && ' Le FTP n\u2019est pas encore configuré : seule la génération est possible.'}
          {resultat.zip_base64 && onRetelecharger && (
            <button
              type="button"
              onClick={onRetelecharger}
              data-testid="cessionpme-retelecharger-zip"
              className="ml-2 underline font-semibold text-indigo-700 hover:text-indigo-900"
            >
              Télécharger à nouveau
            </button>
          )}
        </p>
      )}
      <p className={`mt-2 ${resultat.references_incluses?.length ? 'text-slate-600' : 'text-slate-500'}`}>
        <strong className="text-slate-800">Biens retenus :</strong>{' '}
        {resultat.references_incluses?.length ? resultat.references_incluses.join(', ') : 'aucun'}
      </p>
      <div className="mt-2 space-y-0.5" data-testid="cessionpme-rapport-details">
        {rubriques.map(([testid, libelle, liste]) => (
          <p key={testid} data-testid={testid}
             className={liste?.length ? 'text-slate-600' : 'text-slate-400'}>
            <strong className="text-slate-800">{libelle} :</strong>{' '}
            {liste?.length ? liste.join(', ') : 'rien à signaler'}
          </p>
        ))}
      </div>
      {resultat.biens_ecartes > 0 ? (
        <div className="mt-2" data-testid="cessionpme-ecartes">
          <p className="font-semibold text-amber-700">{resultat.biens_ecartes} bien(s) écarté(s) :</p>
          <ul className="mt-1 space-y-0.5 list-disc list-inside">
            {resultat.ecartes.map(e => (
              <li key={e.reference}><strong>{e.reference}</strong> — {e.raison}</li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="mt-2 text-slate-400" data-testid="cessionpme-ecartes">
          <strong className="text-slate-800">Biens écartés :</strong> aucun
        </p>
      )}
    </div>
  );
};

/** Reconstruit le ZIP reçu en base64 pour que l'utilisateur ouvre le fichier réel. */
export const telechargerArchiveZip = (base64, nom) => {
  const binaire = atob(base64);
  const octets = new Uint8Array(binaire.length);
  for (let i = 0; i < binaire.length; i += 1) octets[i] = binaire.charCodeAt(i);
  const url = URL.createObjectURL(new Blob([octets], { type: 'application/zip' }));
  const lien = document.createElement('a');
  lien.href = url;
  lien.download = nom || 'cessionpme.zip';
  document.body.appendChild(lien);
  lien.click();
  lien.remove();
  URL.revokeObjectURL(url);
};

export default CessionPmeRapport;
